/**
 * ソーシャルリンク表示コンポーネント
 * プロフィールページでユーザーのソーシャルリンクを表示します。
 * 最初の数個のみを表示し、残りは「+N」ボタンでダイアログに全表示します。
 *
 * Note: ソーシャルプラットフォームの定数定義は constants/socialPlatform.tsx を参照
 */
import React, { useState } from 'react';
import { Box, Chip, Link, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { SOCIAL_PLATFORMS } from '../../constants/socialPlatform';
import { SocialLinks as SocialLinksType } from '../../types/UserProfile';

interface SocialLinksProps {
  socialLinks: SocialLinksType;
  maxVisible?: number;
  onCopy?: (message: string) => void;
}

const SocialLinks: React.FC<SocialLinksProps> = ({
  socialLinks,
  maxVisible = 3,
  onCopy
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const activePlatforms = SOCIAL_PLATFORMS.filter(platform => socialLinks?.[platform.key]);
  const visiblePlatforms = activePlatforms.slice(0, maxVisible);
  const hiddenCount = activePlatforms.length - maxVisible;

  const renderChip = (platform: typeof SOCIAL_PLATFORMS[0], value: string, isInDialog = false) => {
    const url = platform.urlTemplate(value);

    const handleCopyId = () => {
      if (!url) {
        navigator.clipboard.writeText(value);
        onCopy?.('IDがコピーされました');
      }
    };

    const chipElement = (
      <Chip
        key={platform.key}
        icon={platform.icon as React.ReactElement}
        label={value}
        size="small"
        sx={{
          height: isInDialog ? '28px' : '24px',
          fontSize: isInDialog ? '0.75rem' : '0.7rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'text.secondary',
          ...(isInDialog && {
            width: '100%',
            justifyContent: 'flex-start',
          }),
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'primary.main',
          },
          '& .MuiChip-icon': {
            marginLeft: isInDialog ? '8px' : '6px',
            color: 'text.secondary'
          },
          ...(isInDialog && {
            '& .MuiChip-label': {
              paddingLeft: '8px',
              paddingRight: '12px',
            }
          }),
          cursor: 'pointer'
        }}
        onClick={!url ? handleCopyId : undefined}
      />
    );

    return url ? (
      <Link
        key={platform.key}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textDecoration: 'none' }}
      >
        {chipElement}
      </Link>
    ) : (
      chipElement
    );
  };

  if (activePlatforms.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap'
      }}>
        {visiblePlatforms.map(platform => {
          const value = socialLinks[platform.key];
          if (!value) return null;
          return renderChip(platform, value);
        })}
        {hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{
              height: '24px',
              fontSize: '0.7rem',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'primary.main',
              }
            }}
          />
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            margin: { xs: '16px', sm: '32px' },
            width: { xs: 'calc(100% - 32px)', sm: '600px' },
          },
          '& .MuiDialogTitle-root': {
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            color: 'text.primary',
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2 },
          },
          '& .MuiDialogContent-root': {
            p: { xs: 2, sm: 3 },
            pt: { xs: 0, sm: 0 },
          }
        }}
      >
        <DialogTitle>ソーシャルリンク</DialogTitle>
        <DialogContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {activePlatforms.map(platform => {
              const value = socialLinks[platform.key];
              if (!value) return null;
              return renderChip(platform, value, true);
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SocialLinks;
